import { select, mouse } from 'd3-selection';
import { extent } from 'd3-array';
import { axisBottom, axisLeft } from 'd3-axis';
import { scaleLinear } from 'd3-scale';
import { line } from 'd3-shape';
import { getLineColor } from '../utils/colors';
import { zip, isNil, uniqueId } from 'lodash-es';

var Spectrum =
/** @class */
function () {
  function Spectrum(svg) {
    this.spectra = [];
    this.offset = 0.1;
    this.showPoints = false;

    this.onSelect = function () {};

    this.id = uniqueId();
    this.margins = {
      left: 60,
      bottom: 50,
      top: 10,
      right: 10
    };
    this.svg = svg;
    this.dataGroup = select(this.svg).append('g').classed('data', true);
    this.axesGroup = select(this.svg).append('g').classed('axes', true);
    select(this.svg).append('g').append('clipPath').attr('id', "clip" + this.id).append('rect').attr('width', this.svg.parentElement.clientWidth).attr('height', this.svg.parentElement.clientHeight - this.margins.top - this.margins.bottom).attr('transform', "translate(0, " + this.margins.top + ")");
    this.dataTooltip = select(this.svg.parentElement).append("div").attr("class", "tooltip").style("opacity", 0).style('position', 'absolute').style('text-align', 'center').style('background-color', 'lightsteelblue').style('border-radius', '0.5rem').style('pointer-events', 'none').style('padding', '0.5rem').style('font-family', 'sans-serif').style('font-size', 'small');
  }

  Spectrum.prototype.setSpectra = function (spectra) {
    this.spectra = spectra;

    if (this.spectra.length > 0) {
      var spectrum = this.spectra[0].spectrum;

      if (isNil(this.plotOptions)) {
        this.plotOptions = {
          xKey: spectrum.getKeys()[0],
          yKey: spectrum.getKeys()[1]
        };
      } else if (!spectrum.hasKey(this.plotOptions.xKey) || !spectrum.hasKey(this.plotOptions.yKey)) {
        this.plotOptions.xKey = null;
        this.plotOptions.yKey = null;
      }
    }

    this.render();
  };

  Spectrum.prototype.setAxes = function (xKey, yKey) {
    this.plotOptions = {
      xKey: xKey,
      yKey: yKey
    };
    this.render();
  };

  Spectrum.prototype.setOffset = function (offset) {
    this.offset = offset;
    this.render();
  };

  Spectrum.prototype.setXRange = function (xRange) {
    this.xRange = xRange;
    this.render();
  };

  Spectrum.prototype.setYRange = function (yRange) {
    this.yRange = yRange;
    this.render();
  };

  Spectrum.prototype.setShowPoints = function (show) {
    this.showPoints = show;
    this.render();
  };

  Spectrum.prototype.setOnSelect = function (onSelect) {
    if (!onSelect) {
      onSelect = function onSelect() {};
    }

    this.onSelect = onSelect;
  };

  Spectrum.prototype.setScales = function () {
    var calculateXRange = isNil(this.xRange);
    var calculateYRange = isNil(this.yRange);
    var xRange = calculateXRange ? [Infinity, -Infinity] : this.xRange;
    var yRange = calculateYRange ? [Infinity, -Infinity] : this.yRange;

    if (calculateXRange || calculateYRange) {
      for (var i = 0; i < this.spectra.length; ++i) {
        var spectrum = this.spectra[i].spectrum;

        if (!spectrum.hasKey(this.plotOptions.xKey) || !spectrum.hasKey(this.plotOptions.yKey)) {
          continue;
        }

        if (calculateXRange) {
          var xR = extent(spectrum.getArray(this.plotOptions.xKey));

          if (!isNil(xR[0]) && !isNil(xR[1])) {
            xRange[0] = Math.min(xRange[0], xR[0]);
            xRange[1] = Math.max(xRange[1], xR[1]);
          }
        }

        if (calculateYRange) {
          var yR = extent(spectrum.getArray(this.plotOptions.yKey));

          if (!isNil(yR[0]) && !isNil(yR[1])) {
            var yOffset = i * this.offset;
            yRange[0] = Math.min(yRange[0], yR[0] + yOffset);
            yRange[1] = Math.max(yRange[1], yR[1] + yOffset);
          }
        }
      }
    }

    var w = this.svg.parentElement.clientWidth;
    var h = this.svg.parentElement.clientHeight;

    if (xRange[0] == Infinity || xRange[1] == -Infinity) {
      xRange = [0, 1];
    } else if (xRange[0] === xRange[1]) {
      xRange[1] = xRange[0] + 1;
    }

    this.xScale = scaleLinear().domain(xRange).range([this.margins.left, w - this.margins.right]); // The spectra are stacked for better visibility, adjust the domain accordingly
    // Add 10% to the range for each additional plot
    // let yDelta = yRange[1] - yRange[0];
    // yDelta *= 1 + 0.1 * (this.spectra.length - 1);
    // yRange[1] = yRange[0] + yDelta;

    if (yRange[0] == Infinity || yRange[1] == -Infinity) {
      yRange = [0, 1];
    } else if (yRange[0] === yRange[1]) {
      yRange[1] = yRange[0] + 1;
    }

    this.yScale = scaleLinear().domain(yRange).range([h - this.margins.bottom, this.margins.top]);
  };

  Spectrum.prototype.drawAxes = function () {
    this.axesGroup.selectAll('g').remove();

    if (this.spectra.length < 1) {
      return;
    }

    var xAxis = axisBottom(this.xScale);
    var yAxis = axisLeft(this.yScale);
    this.axesGroup.append('g').classed('x-axis', true).attr('transform', "translate(0, " + this.yScale(this.yScale.domain()[0]) + ")").call(xAxis);
    this.axesGroup.append('g').classed('y-axis', true).attr('transform', "translate(" + this.xScale(this.xScale.domain()[0]) + ", 0)").call(yAxis); // Add axis labels

    var xLow = this.xScale(this.xScale.domain()[0]);
    var xMid = this.xScale(this.xScale.domain()[0] + (this.xScale.domain()[1] - this.xScale.domain()[0]) / 2);
    var yLow = this.yScale(this.yScale.domain()[0]);
    var yMid = this.yScale(this.yScale.domain()[0] + (this.yScale.domain()[1] - this.yScale.domain()[0]) / 2);
    var xLabel;
    var yLabel;

    if (this.spectra.length > 0) {
      var dp = this.spectra[0].spectrum;
      xLabel = dp.getLabel(this.plotOptions.xKey);
      yLabel = dp.getLabel(this.plotOptions.yKey);
    } else {
      xLabel = this.plotOptions.xKey;
      yLabel = this.plotOptions.yKey;
    }

    this.axesGroup.append('g').attr("transform", "translate(" + xMid + ", " + (yLow + 40) + ")").append("text").attr('text-anchor', 'middle').attr('font-family', 'sans-serif').text(xLabel);
    this.axesGroup.append('g').attr("transform", "translate(" + (xLow - 40) + ", " + yMid + ")").append("text").attr('text-anchor', 'middle').attr('transform', 'rotate(-90)').attr('font-family', 'sans-serif').text(yLabel);
  };

  Spectrum.prototype.drawSpectra = function () {
    var _this = this;

    var plots = this.dataGroup.selectAll('path').data(this.spectra);
    var lineFn = line().x(function (d) {
      return d ? _this.xScale(d[0]) : null;
    }).y(function (d) {
      return d ? _this.yScale(d[1]) : null;
    });
    var colorGen = getLineColor();
    var strokeWidth = 1.5;
    var boldStrokeWidth = 3;

    var onMouseOver = function onMouseOver(d, i, targets) {
      // d is the datum, i is the index in the data
      d;
      select(targets[i]) // .transition()
      .attr('stroke-width', boldStrokeWidth);

      var _a = mouse(targets[i]),
          x = _a[0],
          y = _a[1];

      x += _this.svg.getBoundingClientRect().left; // y += this.svg.getBoundingClientRect().top;

      var sample = _this.spectra[i].sample;

      if (isNil(sample)) {
        return;
      }

      var innerHtml = '';

      for (var _i = 0, _b = Object.entries(sample.composition); _i < _b.length; _i++) {
        var _c = _b[_i],
            key = _c[0],
            val = _c[1];
        innerHtml += key.charAt(0).toUpperCase() + key.slice(1) + ": " + val.toFixed(2) + " <br>";
      }

      _this.dataTooltip.html(innerHtml);

      _this.dataTooltip.style('opacity', 0.9).style('left', x + "px").style('top', y + "px").style('transform', 'translateY(-100%)');
    };

    var onMouseOut = function onMouseOut(d, i, targets) {
      // d is the datum, i is the index in the data
      d;
      i;
      select(targets[i]) // .transition()
      .attr('stroke-width', strokeWidth);

      _this.dataTooltip.style('opacity', 0);
    }; // Update


    plots.datum(function (d, i) {
      if (!d.spectrum.hasKey(_this.plotOptions.xKey) || !d.spectrum.hasKey(_this.plotOptions.yKey)) {
        return [null, null];
      }

      return zip(d.spectrum.getArray(_this.plotOptions.xKey), d.spectrum.getArray(_this.plotOptions.yKey).map(function (val) {
        return val + i * _this.offset;
      }));
    }).attr('d', lineFn).attr('fill', 'none').attr('stroke-width', strokeWidth).attr('stroke', function () {
      var color = colorGen.next().value;
      return "rgba(" + Math.round(color[0] * 255) + ", " + Math.round(color[1] * 255) + ", " + Math.round(color[2] * 255) + ", 0.7)";
    }).on('mouseover', onMouseOver).on('mouseout', onMouseOut); // Enter

    plots.enter().append('path').datum(function (d, i) {
      if (!d.spectrum.hasKey(_this.plotOptions.xKey) || !d.spectrum.hasKey(_this.plotOptions.yKey)) {
        return [null, null];
      }

      return zip(d.spectrum.getArray(_this.plotOptions.xKey), d.spectrum.getArray(_this.plotOptions.yKey).map(function (val) {
        return val + i * _this.offset;
      }));
    }).attr('d', lineFn).attr('fill', 'none').attr('stroke-width', strokeWidth).attr('stroke', function () {
      var color = colorGen.next().value;
      return "rgba(" + Math.round(color[0] * 255) + ", " + Math.round(color[1] * 255) + ", " + Math.round(color[2] * 255) + ", 0.7)";
    }).attr('clip-path', "url(#clip" + this.id + ")").on('mouseover', onMouseOver).on('mouseout', onMouseOut); // Exit

    plots.exit().remove(); // Conditionally plot the points on the paths

    this.dataGroup.selectAll('g').remove();

    if (!this.showPoints) {
      return;
    }

    var pointGroups = this.dataGroup.selectAll('g').data(this.spectra);
    colorGen = getLineColor();
    var points = pointGroups.enter().append('g').datum(function (d, i) {
      if (!d.spectrum.hasKey(_this.plotOptions.xKey) || !d.spectrum.hasKey(_this.plotOptions.yKey)) {
        return [null, null];
      }

      return zip(d.spectrum.getArray(_this.plotOptions.xKey), d.spectrum.getArray(_this.plotOptions.yKey).map(function (val) {
        return val + i * _this.offset;
      }));
    }).attr('fill', function () {
      var color = colorGen.next().value;
      return "rgba(" + Math.round(color[0] * 255) + ", " + Math.round(color[1] * 255) + ", " + Math.round(color[2] * 255) + ", 0.7)";
    }).selectAll('circle').data(function (d) {
      return d;
    });

    var onClick = function onClick(d, i, targets) {
      // d is the datum, i is the index in the data
      d;
      i;
      targets;

      if (_this.onSelect) {
        _this.onSelect(i, d);
      }
    };

    points.enter().append('circle').attr('r', 4).attr('cx', function (d) {
      return _this.xScale(d[0]);
    }).attr('cy', function (d) {
      return _this.yScale(d[1]);
    }).on('click', onClick);
  };

  Spectrum.prototype.render = function () {
    this.setScales();
    this.drawAxes();
    this.drawSpectra();
  };

  return Spectrum;
}();

export { Spectrum };